// chatbotLangGraph.tsx — MexaScreen production chat → LangGraph POST /chat (auto token, panel sync).
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BACKEND_URL, getAccessTokenForOrchestrator, getAuthHeaders } from './backendClient';
import { onAuthStateChange } from './components/authForm';
import { applyOrchestratorChatSync } from './orchestratorSync';
import { STEP_LABELS, } from './langGraphChatTypes';
import { appendStepFiche, resolveStepFiche, shouldShowStepFiche, stepFicheDedupKey, } from './langGraphStepFiche';
import { appendChatNotices, noticeIntroMessage, resolveChatNotices, shouldSuppressOperationalBubble, } from './langGraphChatNotice';
import PipelineDebugDrawer from './langGraphPipelineDebug';
import { LANGGRAPH_PROJECT_ID_KEY, getActiveSessionId, syncLangGraphProjectId, updateProjectTarget, } from './sessionProjects';
const LANGGRAPH_URL = process.env.LANGGRAPH_URL || 'http://localhost:8000';
// ── INFRA: FRONTEND -> ALB --> BACKEND -> AGENTCORE (Option A) ────────────────────────────────────────────
// Dev local  : ORCHESTRATOR_VIA_BACKEND=false → POST http://localhost:8000/chat (FastAPI direct)
// Prod / test: ORCHESTRATOR_VIA_BACKEND=true  → POST {BACKEND_URL}/orchestrator/chat via ALB
const USE_BACKEND_ORCHESTRATOR_PROXY = process.env.ORCHESTRATOR_VIA_BACKEND === 'true';
const CHAT_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat`
    : `${LANGGRAPH_URL}/chat`;
const THREAD_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat/thread`
    : `${LANGGRAPH_URL}/chat/thread`;
// Stores the MexaScreen session number ("1", "2"…).
// Server builds LangGraph config: actor_id = Cognito sub, thread_id = sessionId.
// Same contract on SQLite (local) and AgentCoreMemorySaver (prod).
// Written by sessionProjects.ts when the user switches project in the navbar.
function getLoggedInUsername() {
    var _a;
    try {
        const raw = localStorage.getItem('currentUser');
        if (!raw)
            return null;
        return (_a = JSON.parse(raw).username) !== null && _a !== void 0 ? _a : null;
    }
    catch (_b) {
        return null;
    }
}
function resolveActiveProjectId() {
    var _a;
    const username = getLoggedInUsername();
    if (username) {
        return String(getActiveSessionId(username));
    }
    return (_a = localStorage.getItem(LANGGRAPH_PROJECT_ID_KEY)) !== null && _a !== void 0 ? _a : '1';
}
const ACTION_BANNER_ID = 'langgraph-action-banner';
function getChatHistory() {
    return document.getElementById('chat-history');
}
function appendChatLine(role, text) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const p = document.createElement('p');
    p.innerHTML = `<span class="${role}"></span>${text}`;
    chatHistory.appendChild(p);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function setTyping(show) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const oldTyping = chatHistory.querySelector('.typing');
    if (oldTyping)
        oldTyping.remove();
    if (!show)
        return;
    const typing = document.createElement('p');
    typing.className = 'typing';
    chatHistory.appendChild(typing);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
function removeActionBanner() {
    var _a;
    (_a = document.getElementById(ACTION_BANNER_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
function applyPhaseUI(phase) {
    const inputField = document.getElementById('chat-input');
    const sendButton = document.getElementById('chat-send');
    // Keep chat enabled between steps: resource ops + fiche corrections via natural language.
    const locked = false;
    if (inputField) {
        inputField.disabled = locked;
        inputField.placeholder =
            phase === 'between_steps'
                ? 'Message libre (ex. check receptor) ou cliquez Continuer…'
                : phase === 'job_pending'
                    ? 'Job en cours — vous pouvez envoyer un message ou attendre…'
                    : 'Type your message...';
        inputField.style.opacity = locked ? '0.5' : '1';
    }
    if (sendButton)
        sendButton.disabled = locked;
}
function setSendingLocked(locked, _phase) {
    const sendButton = document.getElementById('chat-send');
    if (sendButton) {
        sendButton.disabled = locked;
    }
    const inputField = document.getElementById('chat-input');
    if (inputField) {
        inputField.disabled = locked;
    }
}
function showActionBanner(phase, nextLabel, currentStep, snap, selectedPdb, onApprove, onLaunchPdb) {
    var _a, _b;
    removeActionBanner();
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const banner = document.createElement('div');
    banner.className = 'langgraph-action-banner';
    banner.id = ACTION_BANNER_ID;
    const receptorId = snap === null || snap === void 0 ? void 0 : snap.receptor_id;
    if (phase === 'brief_complete') {
        banner.innerHTML = `
      <span class="langgraph-action-label">Tous les champs confirmés. Modifie via le chat, ou lance l'étape 2.</span>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">✓ Valider &amp; continuer →</button>
    `;
    }
    else if ((phase === 'between_steps' || phase === 'job_pending') && currentStep === 'receptor_preparation') {
        const pdb = selectedPdb ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' ? snap.selected_pdb : '');
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">PDB ID pour la préparation du récepteur :</span>
      <input
        type="text"
        id="langgraph-pdb-input"
        class="langgraph-pdb-input"
        placeholder="ex. 1MFG"
        maxlength="4"
        value="${escapeHtml(pdb)}"
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">⚗️ Lancer la préparation</button>
    `;
        (_a = banner.querySelector('.langgraph-action-btn-pdb')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-pdb-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || pdb || '').trim().toUpperCase();
            if (!raw || raw.length < 4) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPdb === null || onLaunchPdb === void 0 ? void 0 : onLaunchPdb(raw);
        });
        chatHistory.appendChild(banner);
        chatHistory.scrollTop = chatHistory.scrollHeight;
        return;
    }
    else if (phase === 'between_steps') {
        if (currentStep === 'pdb_selection') {
            const label = nextLabel || STEP_LABELS.receptor_preparation || 'Receptor Preparation';
            banner.innerHTML = `
        <span class="langgraph-action-label">PDB enregistré. Passez à la préparation récepteur pour saisir le PDB et lancer le job.</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">▶ Continuer → ${escapeHtml(label)}</button>
      `;
        }
        else {
            const label = nextLabel || STEP_LABELS[currentStep !== null && currentStep !== void 0 ? currentStep : ''] || 'next step';
            banner.innerHTML = `
        <span class="langgraph-action-label">Prêt pour <strong>${escapeHtml(label)}</strong> ?</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">▶ Continuer → ${escapeHtml(label)}</button>
      `;
        }
    }
    else {
        return;
    }
    (_b = banner.querySelector('.langgraph-action-btn')) === null || _b === void 0 ? void 0 : _b.addEventListener('click', () => {
        removeActionBanner();
        onApprove();
    });
    chatHistory.appendChild(banner);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
function handleChatResponse(data, onApprove, onLaunchPdb, lastStepFicheKeyRef) {
    var _a, _b, _c, _d;
    const phase = ((_a = data.phase) !== null && _a !== void 0 ? _a : 'collecting_brief');
    const notices = resolveChatNotices(data);
    const reply = (_c = (_b = data.ai_response) !== null && _b !== void 0 ? _b : data.message) !== null && _c !== void 0 ? _c : '';
    const suppressBubble = shouldSuppressOperationalBubble(data, notices);
    if (notices.length && suppressBubble) {
        const intro = noticeIntroMessage(notices);
        if (intro)
            appendChatLine('bot', intro);
    }
    if (notices.length)
        appendChatNotices(notices);
    if (reply && !suppressBubble)
        appendChatLine('bot', reply);
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(data);
        if (fiche) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(data, fiche);
        }
    }
    if (phase === 'brief_complete' || phase === 'between_steps') {
        showActionBanner(phase, (_d = data.next_step_label) !== null && _d !== void 0 ? _d : '', data.current_step, data.state_snapshot, data.selected_pdb, onApprove, onLaunchPdb);
    }
    applyPhaseUI(phase);
    return phase;
}
/** Replay checkpoint messages + restore phase banner/fiche without re-sending to the graph. */
function applyThreadRestore(data, onApprove, onLaunchPdb, lastStepFicheKeyRef) {
    var _a, _b, _c;
    const chatHistory = getChatHistory();
    if (chatHistory)
        chatHistory.innerHTML = '';
    removeActionBanner();
    for (const msg of (_a = data.messages) !== null && _a !== void 0 ? _a : []) {
        appendChatLine(msg.role, msg.content);
    }
    const chat = data.chat;
    if (!chat) {
        applyPhaseUI('collecting_brief');
        return 'collecting_brief';
    }
    const phase = ((_b = chat.phase) !== null && _b !== void 0 ? _b : 'collecting_brief');
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(chat);
        if (fiche) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(chat, fiche);
        }
    }
    if (phase === 'brief_complete' || phase === 'between_steps' || (phase === 'job_pending' && chat.current_step === 'receptor_preparation')) {
        showActionBanner(phase, (_c = chat.next_step_label) !== null && _c !== void 0 ? _c : '', chat.current_step, chat.state_snapshot, chat.selected_pdb, onApprove, onLaunchPdb);
    }
    applyPhaseUI(phase);
    return phase;
}
function restoreChatThread(projectId, onApprove, onLaunchPdb, lastStepFicheKeyRef, setLastResponse, currentPhaseRef) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const chatHistory = getChatHistory();
        if (!chatHistory)
            return;
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken)
                return;
            const response = yield fetch(`${THREAD_URL}?project_id=${encodeURIComponent(projectId)}`, {
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
            });
            const data = yield response.json().catch(() => ({ exists: false }));
            if (!response.ok || !data.exists) {
                chatHistory.innerHTML = '';
                removeActionBanner();
                currentPhaseRef.current = 'collecting_brief';
                applyPhaseUI('collecting_brief');
                setLastResponse(null);
                return;
            }
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (username && ((_b = (_a = data.chat) === null || _a === void 0 ? void 0 : _a.project_brief) === null || _b === void 0 ? void 0 : _b.target)) {
                updateProjectTarget(username, activeId, data.chat.project_brief.target);
            }
            setLastResponse((_c = data.chat) !== null && _c !== void 0 ? _c : null);
            const phase = applyThreadRestore(data, onApprove, onLaunchPdb, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            if (data.chat) {
                yield ((_d = window.applyOrchestratorChatSync) !== null && _d !== void 0 ? _d : applyOrchestratorChatSync)(data.chat);
            }
        }
        catch (err) {
            console.warn('[ChatbotLangGraph] Failed to restore chat thread:', err);
            if (chatHistory)
                chatHistory.innerHTML = '';
            removeActionBanner();
            currentPhaseRef.current = 'collecting_brief';
            applyPhaseUI('collecting_brief');
            setLastResponse(null);
        }
    });
}
const ChatbotLangGraph = () => {
    const isMountedRef = useRef(true);
    const abortRef = useRef(null);
    const currentPhaseRef = useRef('collecting_brief');
    const sendMessageRef = useRef(() => __awaiter(void 0, void 0, void 0, function* () { }));
    const lastStepFicheKeyRef = useRef('');
    const [debugOpen, setDebugOpen] = useState(false);
    const [lastResponse, setLastResponse] = useState(null);
    const sendMessage = useCallback((text_1, ...args_1) => __awaiter(void 0, [text_1, ...args_1], void 0, function* (text, approved = false) {
        var _a, _b, _c;
        if (!text || !isMountedRef.current)
            return;
        removeActionBanner();
        appendChatLine('user', text);
        const phaseBeforeSend = currentPhaseRef.current;
        setSendingLocked(true, phaseBeforeSend);
        setTyping(true);
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        abortRef.current = new AbortController();
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken) {
                setTyping(false);
                appendChatLine('bot', 'Session expired. Please sign in again to use the assistant.');
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Navbar activeSessionId drives LangGraph thread_id (actor_id = Cognito sub on server).
            const projectId = resolveActiveProjectId();
            const response = yield fetch(CHAT_URL, {
                method: 'POST',
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
                body: JSON.stringify({
                    user_message: text,
                    project_id: projectId,
                    ms_token: msToken,
                    approved,
                }),
                signal: abortRef.current.signal,
            });
            const data = yield response.json().catch(() => ({}));
            if (!isMountedRef.current)
                return;
            setTyping(false);
            if (!response.ok) {
                const detail = data.detail;
                const detailText = detail
                    ? typeof detail === 'string'
                        ? detail
                        : JSON.stringify(detail)
                    : '';
                appendChatLine('bot', data.error || detailText || `Chat error (${response.status})`);
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Keep frontend active session authoritative until backend supports multi-workspace.
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (data.ms_session_id != null && data.ms_session_id === activeId) {
                syncLangGraphProjectId(activeId);
            }
            else if (data.project_id === String(activeId)) {
                syncLangGraphProjectId(activeId);
            }
            if (username && ((_b = data.project_brief) === null || _b === void 0 ? void 0 : _b.target)) {
                updateProjectTarget(username, activeId, data.project_brief.target);
            }
            setLastResponse(data);
            const phase = handleChatResponse(data, () => {
                void sendMessageRef.current('continue', true);
            }, (pdb) => {
                void sendMessageRef.current(pdb, true);
            }, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            yield ((_c = window.applyOrchestratorChatSync) !== null && _c !== void 0 ? _c : applyOrchestratorChatSync)(data);
        }
        catch (err) {
            if (!isMountedRef.current)
                return;
            if (err instanceof DOMException && err.name === 'AbortError')
                return;
            setTyping(false);
            console.error('[ChatbotLangGraph] /chat failed:', err);
            appendChatLine('bot', 'Could not reach the assistant. Is LangGraph running on port 8000?');
            applyPhaseUI(currentPhaseRef.current);
        }
        finally {
            if (isMountedRef.current) {
                setSendingLocked(false, currentPhaseRef.current);
                if (currentPhaseRef.current !== 'between_steps') {
                    const inputField = document.getElementById('chat-input');
                    inputField === null || inputField === void 0 ? void 0 : inputField.focus();
                }
            }
        }
    }), []);
    sendMessageRef.current = sendMessage;
    useEffect(() => {
        isMountedRef.current = true;
        const chatHistory = getChatHistory();
        if (chatHistory)
            chatHistory.innerHTML = '';
        const debugBtn = document.getElementById('chat-pipeline-debug-btn');
        if (debugBtn)
            debugBtn.style.display = '';
        const onApprove = () => {
            void sendMessageRef.current('continue', true);
        };
        const onLaunchPdb = (pdb) => {
            void sendMessageRef.current(pdb, true);
        };
        void restoreChatThread(resolveActiveProjectId(), onApprove, onLaunchPdb, lastStepFicheKeyRef, setLastResponse, currentPhaseRef);
        const handleSend = () => {
            const inputField = document.getElementById('chat-input');
            const text = inputField === null || inputField === void 0 ? void 0 : inputField.value.trim();
            if (!text || (inputField === null || inputField === void 0 ? void 0 : inputField.disabled))
                return;
            inputField.value = '';
            void sendMessageRef.current(text, false);
        };
        const handleKeyDown = (e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.isComposing)
                return;
            e.preventDefault();
            e.stopPropagation();
            handleSend();
        };
        const sendButton = document.getElementById('chat-send');
        const inputField = document.getElementById('chat-input');
        sendButton === null || sendButton === void 0 ? void 0 : sendButton.addEventListener('click', handleSend);
        inputField === null || inputField === void 0 ? void 0 : inputField.addEventListener('keydown', handleKeyDown);
        const onDebugClick = () => setDebugOpen(true);
        debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.addEventListener('click', onDebugClick);
        const unsubscribeAuth = onAuthStateChange((_user, authenticated) => {
            if (!authenticated)
                setDebugOpen(false);
        });
        return () => {
            var _a;
            setDebugOpen(false);
            unsubscribeAuth();
            isMountedRef.current = false;
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            sendButton === null || sendButton === void 0 ? void 0 : sendButton.removeEventListener('click', handleSend);
            inputField === null || inputField === void 0 ? void 0 : inputField.removeEventListener('keydown', handleKeyDown);
            debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.removeEventListener('click', onDebugClick);
            if (debugBtn)
                debugBtn.style.display = 'none';
            removeActionBanner();
        };
    }, []);
    return createPortal(React.createElement(PipelineDebugDrawer, { open: debugOpen, onClose: () => setDebugOpen(false), data: lastResponse }), document.body);
};
export default ChatbotLangGraph;
